import { refreshToken } from "./tokens";
import { notificationService } from "@entities/notification-toast/notificationService";

let isRefreshing = false;
let failedQueue = [];
let isRedirectingToLogin = false;

// Resolves each queued request with its own config (not just the token) so we
// can mark it `_retry` before replaying it — otherwise a request that 401s
// again after the "successful" refresh re-enters this whole flow and starts
// another refresh cycle, instead of failing once.
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      config._retry = true;
      config.headers.Authorization = `Bearer ${token}`;
      resolve(config);
    }
  });
  failedQueue = [];
};

const redirectToLogin = () => {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  window.location.href = "/auth/login";
};

export const setupResponseInterceptors = (axiosAuth) => {
  axiosAuth.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 429) {
        notificationService.show("Too many requests. Please try again later.", 429);
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Refresh already failed for this session — don't start another
        // refresh cycle while the redirect to /auth/login is in flight.
        if (isRedirectingToLogin) return Promise.reject(error);

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config: originalRequest });
          }).then((config) => axiosAuth(config));
        }

        originalRequest._retry = true;
        isRefreshing = true;
        try {
          const newToken = await refreshToken();
          axiosAuth.defaults.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosAuth(originalRequest);
        } catch (err) {
          processQueue(err, null);
          redirectToLogin();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};
